import { useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { fileUrl } from "@/lib/api";

export default function Lightbox({ images, index, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const current = images[index];
  if (!current) return null;

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-[#05070d]/95 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-testid="gallery-lightbox"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2.5 text-white transition-colors hover:bg-husko-magenta hover:text-void"
      >
        <X size={20} />
      </button>
      <button
        data-testid="lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          onNav(-1);
        }}
        aria-label="Previous image"
        className="absolute left-3 z-10 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-husko-cyan hover:text-void md:left-6"
      >
        <ChevronLeft size={22} />
      </button>
      <figure className="max-w-[92vw] md:max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <motion.img
          key={current.id}
          src={fileUrl(current.storage_path)}
          alt={current.caption || "Gallery image"}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="max-h-[76vh] w-auto max-w-full rounded-2xl border border-white/10 object-contain shadow-2xl"
          data-testid="lightbox-image"
        />
        <figcaption className="mt-3 flex items-center justify-between gap-4 text-sm text-slate-300">
          <span>{current.caption || "No caption — just fluff"}</span>
          <span className="shrink-0 font-mono text-xs text-husko-cyan">
            {index + 1} / {images.length}
          </span>
        </figcaption>
      </figure>
      <button
        data-testid="lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          onNav(1);
        }}
        aria-label="Next image"
        className="absolute right-3 z-10 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-husko-cyan hover:text-void md:right-6"
      >
        <ChevronRight size={22} />
      </button>
    </motion.div>
  );
}
